/*
 * Simple slider controller.
 *
 * Use this file for the boxed carousel layout with arrows, pagination, and
 * thumbnail rail.
 *
 * Why this file exists:
 * - keeps the simple-slider interaction isolated from the shared core
 * - preserves the current slider behavior under a clearer layout-specific name
 * - lets us compare this layout against more editorial gallery treatments
 */

(function () {
  const ProductGalleryCore = window.ProductGalleryCore;
  if (!ProductGalleryCore) return;

  class ProductSimpleSlider extends ProductGalleryCore {
    bindLayoutEvents() {
      this.handlePrevClick = () => this.goToRelative(-1);
      this.handleNextClick = () => this.goToRelative(1);
      this.handleViewerKeydown = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goToRelative(-1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goToRelative(1);
        }
      };

      this.slideContainer?.addEventListener('scroll', this.handleSlideScroll, { passive: true });
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', this.handlePrevClick);
      this.querySelector('[data-gallery-next]')?.addEventListener('click', this.handleNextClick);
      this.mainViewer?.addEventListener('keydown', this.handleViewerKeydown);
    }

    unbindLayoutEvents() {
      this.slideContainer?.removeEventListener('scroll', this.handleSlideScroll, { passive: true });
      this.querySelector('[data-gallery-prev]')?.removeEventListener('click', this.handlePrevClick);
      this.querySelector('[data-gallery-next]')?.removeEventListener('click', this.handleNextClick);
      this.mainViewer?.removeEventListener('keydown', this.handleViewerKeydown);
    }

    paintPagination() {
      if (!this.pagination) return;

      this.pagination.innerHTML = '';

      this.currentMediaIds.forEach((mediaId, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-simple-slider__page-dot';
        button.setAttribute('aria-label', `View image ${index + 1}`);
        button.dataset.mediaId = mediaId;
        button.setAttribute('data-gallery-dot', '');
        button.addEventListener('click', () => this.goToIndex(index));
        this.pagination.appendChild(button);
      });
    }

    paintActiveMedia() {
      super.paintActiveMedia();

      const activeMediaId = this.currentMediaIds[this.currentIndex] || this.currentMediaIds[0];
      const activeThumb = this.thumbs.find((thumb) => String(thumb.dataset.mediaId) === String(activeMediaId));
      this.scrollThumbRailToActiveThumb(activeThumb);
    }

    scrollThumbRailToActiveThumb(activeThumb) {
      if (!activeThumb || !this.thumbContainer) return;

      const targetLeft = activeThumb.offsetLeft - (this.thumbContainer.clientWidth - activeThumb.clientWidth) / 2;
      this.thumbContainer.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: 'smooth',
      });
    }

    syncViewerPosition({ behavior = 'smooth' } = {}) {
      if (!this.slideContainer || !this.currentMediaIds.length) return;

      const mediaId = this.currentMediaIds[this.currentIndex];
      const slide = this.getSlideByMediaId(mediaId);
      if (!slide) return;

      this.slideContainer.scrollTo({
        left: slide.offsetLeft,
        behavior,
      });
    }

    handleSlideScroll = () => {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (!this.slideContainer || !this.currentMediaIds.length) return;

        const nextIndex = this.getNearestIndexFromScroll();
        if (nextIndex === this.currentIndex) return;

        this.currentIndex = nextIndex;
        this.paintActiveMedia();
      });
    };
  }

  if (!customElements.get('product-simple-slider')) {
    customElements.define('product-simple-slider', ProductSimpleSlider);
  }
})();
